import { useLocation, useNavigate } from 'react-router-dom';
import { useMemo, createElement } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HomeIcon from '@mui/icons-material/Home';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ChatIcon from '@mui/icons-material/Chat';
import type { ReactElement, ComponentType } from 'react';

export interface NavigationItem {
  label: string;
  value: string;
  path: string;
  icon: ReactElement;
  ariaLabel: string;
}

export function useGlobalNavigation() {
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems: NavigationItem[] = useMemo(
    () => [
      {
        label: 'Rechercher',
        value: 'search',
        path: '/search',
        icon: createElement(SearchIcon as ComponentType),
        ariaLabel: 'Search for building materials',
      },
      {
        label: 'Publier',
        value: 'publish',
        path: '/publish',
        icon: createElement(AddCircleOutlineIcon as ComponentType),
        ariaLabel: 'Publish new offer',
      },
      {
        label: 'Home',
        value: 'home',
        path: '/',
        icon: createElement(HomeIcon as ComponentType),
        ariaLabel: 'Go to home page',
      },
      {
        label: 'Favoris',
        value: 'favorites',
        path: '/favorites',
        icon: createElement(FavoriteIcon as ComponentType),
        ariaLabel: 'View your favorites',
      },
      {
        label: 'Messages',
        value: 'messages',
        path: '/messages',
        icon: createElement(ChatIcon as ComponentType),
        ariaLabel: 'View your messages',
      },
    ],
    []
  );

  // Pattern matching for nested routes
  const activeTab = useMemo(() => {
    const pathname = location.pathname;
    const from = (location.state as { from?: string } | null)?.from;

    if (pathname === '/') return 'home';
    if (pathname.startsWith('/search')) return 'search';
    if (pathname.startsWith('/offers/')) {
      if (from === 'favorites') return 'favorites';
      if (from === 'my-publications' || from === 'purchases' || from === 'alerts') return 'home';
      return 'search'; // default: came from search
    }
    if (pathname.startsWith('/publish')) return 'publish';
    if (pathname.startsWith('/favorites')) return 'favorites';
    if (pathname.startsWith('/messages')) return 'messages';
    if (pathname.startsWith('/alerts')) return 'home'; // Alerts has no footer tab — falls back to home highlight

    return 'home'; // Default fallback
  }, [location.pathname, location.state]);

  const handleNavigate = (_event: React.SyntheticEvent, newValue: string) => {
    const item = navigationItems.find(item => item.value === newValue);
    if (item) {
      navigate(item.path);
    }
  };

  return {
    activeTab,
    handleNavigate,
    navigationItems,
  };
}
